const datefns = require('date-fns');
const { WEEK_DAYS_LIST } = require('../enums');
const DateLogic = Object.create(null);

/** @typedef {import('../models/CloseDayModel.js').CloseDayItem } CloseDayItem */
/** @typedef {import('../models/OpenDayModel.js').OpenDayItem } OpenDayItem */
/** @typedef {import('../services/OpenDayService.js').OpenDayObj } OpenDayObj */

/** Nombre de jours maximum sur lesquels on recherche la prochaine ouverture (pour couvrir les longues fermetures) */
const NB_DAYS_MAX_NEXT_OPENING = 60;

DateLogic.isClosed = isClosed;
DateLogic.isClosedDateRange = isClosedDateRange;
DateLogic.getCurrentOpenDay = getCurrentOpenDay;
DateLogic.getNextOpeningDate = getNextOpeningDate;

/**
 * Permet de savoir si le jour en paramètre est fermé en prenant en compte les fermetures de toute la journée.
 * Si les jours d'ouverture sont fournis, la date est aussi considérée comme fermée lorsqu'elle n'est dans aucune plage horaire d'ouverture de son jour de la semaine
 * @param {Array<CloseDayItem>} closeDays - Liste des journées de fermeture
 * @param {Date} date - Date à vérifier
 * @param {?OpenDayObj} [openDaysObj] - Objet contenant les informations des jours d'ouverture
 * @returns {boolean} - true si le jour est fermé, false sinon
 * TU DONE 🗸
 */
function isClosed(closeDays, date, openDaysObj = null) {
  const isInCloseDay = closeDays.some(closeDay => datefns.isWithinInterval(date, _getCloseDayInterval(closeDay)));

  if (isInCloseDay || !openDaysObj) {
    return isInCloseDay;
  }

  return !_getOpenDayAtDate(openDaysObj.openDays || [], date);
}

/**
 * Permet de savoir si une plage de dates chevauche une journée de fermeture
 * @param {Array<CloseDayItem>} closeDays - Liste des journées de fermeture
 * @param {Date} startDate - Date de début de la plage
 * @param {Date} endDate - Date de fin de la plage
 * @returns {boolean} - true si la plage chevauche une fermeture, false sinon
 * TU DONE 🗸
 */
function isClosedDateRange(closeDays, startDate, endDate) {
  return closeDays.some(closeDay => datefns.areIntervalsOverlapping({ start: startDate, end: endDate }, _getCloseDayInterval(closeDay)));
}

/**
 * Permet de récupérer la plage horaire d'ouverture en cours à une date, en tenant compte des jours de fermeture
 * @param {Array<CloseDayItem>} closeDays - Liste des journées de fermeture
 * @param {Date} date - Date à vérifier
 * @param {?OpenDayObj} openDaysObj - Objet contenant les informations des jours d'ouverture
 * @returns {?OpenDayItem} - Plage horaire d'ouverture en cours, null si fermé à cette date
 * TU DONE 🗸
 */
function getCurrentOpenDay(closeDays, date, openDaysObj) {
  if (!openDaysObj || isClosed(closeDays, date, openDaysObj)) {
    return null;
  }

  return _getOpenDayAtDate(openDaysObj.openDays || [], date);
}

/**
 * Permet de récupérer la date de la prochaine ouverture après une date, en tenant compte des jours de fermeture.
 * Si le début d'une plage horaire est dans une fermeture, la réouverture est à la fin de la fermeture si elle est avant la fin de la plage (ex: plage 13h00-22h00, fermeture 08h00-20h30 => 20h30)
 * @param {Array<CloseDayItem>} closeDays - Liste des journées de fermeture
 * @param {Date} date - Date à partir de laquelle on recherche la prochaine ouverture
 * @param {?OpenDayObj} openDaysObj - Objet contenant les informations des jours d'ouverture
 * @returns {?Date} - Date de la prochaine ouverture, null si aucun jour d'ouverture ou aucune ouverture sur les 60 prochains jours
 * TU DONE 🗸
 */
function getNextOpeningDate(closeDays, date, openDaysObj) {
  const openDays = openDaysObj?.openDays || [];

  for (let i = 0; i <= NB_DAYS_MAX_NEXT_OPENING && openDays.length; i++) {
    const day = datefns.addDays(date, i);
    const openingDate = _getDayOpenDays(openDays, day)
      .map(openDay => _getOpeningDateInOpenDay(closeDays, date, day, openDay))
      .find(Boolean);

    if (openingDate) {
      return openingDate;
    }
  }

  return null;
}

/* --------------- PRIVATE FUNCTION --------------- */
/**
 * Permet de récupérer l'intervalle d'une fermeture : du début à la fin de la journée pour une fermeture de toute la journée, sinon en respectant les heures et minutes des dates
 * @param {CloseDayItem} closeDay - Journée de fermeture
 * @returns {{start: Date, end: Date}} - Intervalle de la fermeture
 * @private
 */
function _getCloseDayInterval(closeDay) {
  if (closeDay.all_day) {
    return { start: datefns.startOfDay(new Date(closeDay.start_date)), end: datefns.endOfDay(new Date(closeDay.end_date)) };
  }

  return { start: new Date(closeDay.start_date), end: new Date(closeDay.end_date) };
}

/**
 * Permet de récupérer la fin de la fermeture en cours à une date
 * @param {Array<CloseDayItem>} closeDays - Liste des journées de fermeture
 * @param {Date} date - Date à vérifier
 * @returns {?Date} - Fin de la fermeture en cours, null si aucune fermeture en cours (la fin d'une fermeture n'est pas considérée comme en cours)
 * @private
 */
function _getCloseDayEnd(closeDays, date) {
  const interval = closeDays.map(_getCloseDayInterval).find(closeDayInterval => date >= closeDayInterval.start && date < closeDayInterval.end);

  return interval ? interval.end : null;
}

/**
 * Permet de récupérer les plages horaires d'ouverture du jour de la semaine d'une date, triées par heure de début
 * @param {Array<OpenDayItem>} openDays - Jours d'ouverture, plusieurs lignes possibles pour un même jour
 * @param {Date} date - Date dont on veut les plages horaires
 * @returns {Array<OpenDayItem>} - Plages horaires d'ouverture du jour
 * @private
 */
function _getDayOpenDays(openDays, date) {
  // NOTE getDay() commence au dimanche (0) alors que WEEK_DAYS_LIST commence au lundi
  const dayValue = WEEK_DAYS_LIST[(date.getDay() + 6) % 7].value;

  return openDays.filter(openDay => openDay.day === dayValue).sort((a, b) => a.hour_start.localeCompare(b.hour_start));
}

/**
 * Permet de récupérer la plage horaire d'ouverture qui contient une date
 * @param {Array<OpenDayItem>} openDays - Jours d'ouverture, plusieurs lignes possibles pour un même jour
 * @param {Date} date - Date à vérifier
 * @returns {?OpenDayItem} - Plage horaire d'ouverture qui contient la date, null sinon
 * @private
 */
function _getOpenDayAtDate(openDays, date) {
  const hour = datefns.format(date, 'HH:mm');

  return _getDayOpenDays(openDays, date).find(openDay => openDay.hour_start <= hour && hour < openDay.hour_end) || null;
}

/**
 * Permet de récupérer la date d'ouverture dans une plage horaire, après une date et en dehors des fermetures
 * @param {Array<CloseDayItem>} closeDays - Liste des journées de fermeture
 * @param {Date} date - Date à partir de laquelle on recherche l'ouverture
 * @param {Date} day - Jour de la plage horaire
 * @param {OpenDayItem} openDay - Plage horaire d'ouverture
 * @returns {?Date} - Date d'ouverture dans la plage, null si la plage est passée ou entièrement fermée
 * @private
 */
function _getOpeningDateInOpenDay(closeDays, date, day, openDay) {
  const rangeEnd = _getDateWithHour(day, openDay.hour_end);
  let openingDate = datefns.max([ _getDateWithHour(day, openDay.hour_start), date ]);
  let closeDayEnd = _getCloseDayEnd(closeDays, openingDate);

  // NOTE tant que la date d'ouverture est dans une fermeture, on la décale à la fin de la fermeture
  while (closeDayEnd && openingDate < rangeEnd) {
    openingDate = closeDayEnd;
    closeDayEnd = _getCloseDayEnd(closeDays, openingDate);
  }

  return openingDate < rangeEnd ? openingDate : null;
}

/**
 * Permet de récupérer une date à une heure donnée
 * @param {Date} date - Jour de la date
 * @param {string} hour - Heure au format HH:mm
 * @returns {Date} - Date à l'heure donnée
 * @private
 */
function _getDateWithHour(date, hour) {
  const [ hours, minutes ] = hour.split(':');

  return datefns.setMinutes(datefns.setHours(datefns.startOfDay(date), parseInt(hours, 10)), parseInt(minutes, 10));
}
/* --------------- /PRIVATE FUNCTION --------------- */

module.exports = DateLogic;
